import { Ref } from 'vue';
import { logger, LogCategory } from '@/services/logger';

/**
 * Standard error types for better error categorization
 */
export enum ErrorType {
  AUTHENTICATION = 'Authentication Error',
  DATABASE = 'Database Error',
  STORAGE = 'Storage Error',
  NETWORK = 'Network Error',
  VALIDATION = 'Validation Error',
  UNKNOWN = 'Unknown Error'
}

/**
 * Standardized error interface
 */
export interface AppError {
  type: ErrorType;
  message: string;
  originalError?: unknown;
  code?: string;
  context?: Record<string, any>;
}

/**
 * Create a standardized application error
 */
export function createAppError(
  message: string,
  type: ErrorType = ErrorType.UNKNOWN,
  originalError?: unknown,
  context?: Record<string, any>
): AppError {
  // Extract error code if available
  let code: string | undefined;
  if (originalError && typeof originalError === 'object' && 'code' in originalError) {
    code = (originalError as any).code;
  }

  return {
    type,
    message,
    originalError,
    code,
    context
  };
}

/**
 * Handle errors consistently across the application
 * @param err The error to handle
 * @param errorRef Optional Vue ref to update with the error
 * @param type Error type for categorization
 * @param message Optional custom message
 * @param context Additional context information
 * @returns The standardized AppError
 */
export function handleError(
  err: unknown,
  errorRef?: Ref<Error | AppError | null>,
  type: ErrorType = ErrorType.UNKNOWN,
  message?: string,
  context?: Record<string, any>
): AppError {
  // Create a standardized error message
  const errorMessage = message || (err instanceof Error ? err.message : 'An unexpected error occurred');
  
  // Create a standardized error object
  const appError = createAppError(errorMessage, type, err, context);
  
  // Map ErrorType to LogCategory
  let logCategory: LogCategory;
  switch (type) {
    case ErrorType.AUTHENTICATION:
      logCategory = LogCategory.AUTH;
      break;
    case ErrorType.DATABASE:
      logCategory = LogCategory.DATABASE;
      break;
    case ErrorType.STORAGE:
      logCategory = LogCategory.STORAGE;
      break;
    case ErrorType.NETWORK:
      logCategory = LogCategory.NETWORK;
      break;
    default:
      logCategory = LogCategory.GENERAL;
  }
  
  // Log the error
  logger.error(`${appError.type}: ${appError.message}`, logCategory, {
    error: err,
    ...(context || {})
  });
  
  // Update the error ref if provided
  if (errorRef) {
    errorRef.value = appError;
  }
  
  return appError;
}

/**
 * Wrap an async function with standardized error handling
 * @param operation The async operation to execute
 * @param errorRef Optional Vue ref to update with any error
 * @param type Error type for categorization
 * @param errorMessage Optional custom error message
 * @param context Additional context information
 * @returns A promise that resolves to the operation result or rejects with a standardized error
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  errorRef?: Ref<Error | AppError | null>,
  type: ErrorType = ErrorType.UNKNOWN,
  errorMessage?: string,
  context?: Record<string, any>
): Promise<T> {
  try {
    return await operation();
  } catch (err) {
    const appError = handleError(err, errorRef, type, errorMessage, context);
    throw appError;
  }
}